/**
 * The map part of a share code (`codec.ts`): a table of the cave's symbols, most used first, then
 * tokens that are either a run of one symbol or a copy of cells seen earlier (LZ77-style), so rows
 * and rooms that repeat cost a few bits. Pure.
 */

import type { BitReader, BitWriter } from './codecBits';
import { BadCode, gammaBits } from './codecBits';

/** Legend characters by 5-bit code. Code 31 escapes anything else as text, so no cave is lost. */
const SYMBOLS = [...'Wwm. rdPXafFbB?xs123456t'];
const SYMBOL_CODE = new Map(SYMBOLS.map((s, i) => [s, i]));
const LITERAL = 31;
const MAX_LITERAL = 64;
/** How far back a copy may reach, in cells: two 80-wide rows and then some. */
const WINDOW = 1024;

export function writeSymbol(out: BitWriter, s: string): void {
  const code = s.length === 1 ? SYMBOL_CODE.get(s) : undefined;
  if (code !== undefined) return out.bits(code, 5);
  out.bits(LITERAL, 5);
  out.text(s);
}

export function readSymbol(r: BitReader): string {
  const code = r.bits(5);
  if (code === LITERAL) return r.text(MAX_LITERAL);
  const s = SYMBOLS[code];
  if (s === undefined) throw new BadCode('the code is damaged (unknown element)');
  return s;
}

function runAt(cells: readonly string[], i: number, limit: number): number {
  let run = 1;
  while (run < limit && cells[i + run] === cells[i]) run++;
  return run;
}

/** Cells in reading order. */
export function writeCells(out: BitWriter, cells: readonly string[]): void {
  const freq = new Map<string, number>();
  for (const c of cells) freq.set(c, (freq.get(c) ?? 0) + 1);
  const table = [...freq.keys()].sort((a, b) => (freq.get(b) ?? 0) - (freq.get(a) ?? 0) || (a < b ? -1 : 1));
  const index = new Map(table.map((s, i) => [s, i]));
  out.int(table.length);
  for (const s of table) writeSymbol(out, s);

  const symbolBits = (i: number): number => gammaBits((index.get(cells[i] ?? '') ?? 0) + 1);
  const runBits = (i: number, run: number): number => 1 + symbolBits(i) + gammaBits(run);
  /** What `len` cells from `i` cost as runs. */
  const spanBits = (i: number, len: number): number => {
    let bits = 0;
    for (let k = i; k < i + len; ) {
      const run = runAt(cells, k, i + len - k);
      bits += runBits(k, run);
      k += run;
    }
    return bits;
  };

  for (let i = 0; i < cells.length; ) {
    const run = runAt(cells, i, cells.length - i);
    let best = { len: 0, offset: 0, gain: 0 };
    for (let offset = 1; offset <= Math.min(i, WINDOW); offset++) {
      let len = 0;
      while (i + len < cells.length && cells[i + len] === cells[i + len - offset]) len++;
      if (len <= run || len <= best.len) continue;
      const gain = spanBits(i, len) - (1 + gammaBits(len) + gammaBits(offset));
      if (gain > best.gain) best = { len, offset, gain };
    }
    if (best.gain > 0) {
      out.bits(1, 1);
      out.gamma(best.len);
      out.gamma(best.offset);
      i += best.len;
    } else {
      out.bits(0, 1);
      out.gamma((index.get(cells[i] ?? '') ?? 0) + 1);
      out.gamma(run);
      i += run;
    }
  }
}

/** `total` cells in reading order. */
export function readCells(r: BitReader, total: number): string[] {
  const n = r.count(SYMBOLS.length + MAX_LITERAL, 'symbols');
  const table: string[] = [];
  for (let i = 0; i < n; i++) {
    const s = readSymbol(r);
    if (s.length !== 1) throw new BadCode('the code is damaged (map symbol)');
    table.push(s);
  }
  const cells: string[] = [];
  while (cells.length < total) {
    if (r.bit() === 0) {
      const s = table[r.gamma() - 1];
      const run = r.gamma();
      if (s === undefined || cells.length + run > total) throw new BadCode('the code is damaged (map)');
      for (let k = 0; k < run; k++) cells.push(s);
    } else {
      const len = r.gamma();
      const offset = r.gamma();
      if (offset > cells.length || cells.length + len > total) throw new BadCode('the code is damaged (map copy)');
      for (let k = 0; k < len; k++) cells.push(cells[cells.length - offset] ?? 'W');
    }
  }
  return cells;
}
